import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import { findMyDriverProfile } from '@/lib/myDriverProfile';
import { toast } from 'sonner';
import { User, CreditCard, Truck, Save, Eye, EyeOff, Headphones, DollarSign, TrendingUp, Camera, IdCard, HeartPulse, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import AccountSecurityCard from '@/components/AccountSecurityCard';
import TrimbleAddressAutocomplete from '@/components/TrimbleAddressAutocomplete';
import UploadField from '@/components/UploadField';
import { uploadAvatar } from '@/lib/teamChat';
import { avatarColorClasses } from '@/lib/avatarColor';
import { numOrDefault } from '@/lib/invoiceHelpers';
import { extractDriverCredential } from '@/lib/parseDriverCredential';
import CompleteTaxFormModal from '@/components/CompleteTaxFormModal';

const SectionCard = ({ icon: Icon, title, children }) => (
  <div className="glass-card p-6 space-y-4">
    <div className="flex items-center gap-3 pb-3 border-b border-border">
      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
        <Icon className="w-4 h-4 text-primary" />
      </div>
      <h3 className="font-space font-semibold text-foreground">{title}</h3>
    </div>
    {children}
  </div>
);

const Field = ({ label, value, onChange, type = 'text', placeholder = '', autoComplete }) => (
  <div>
    <label className="text-xs text-muted-foreground block mb-1">{label}</label>
    <Input
      type={type}
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      className="text-foreground"
    />
  </div>
);

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID',
  'IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO',
  'MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA',
  'RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

const ReadRow = ({ label, children }) => (
  <div>
    <label className="text-xs text-muted-foreground block mb-1">{label}</label>
    <div className="text-foreground text-sm py-2 px-3 rounded-lg bg-transparent border border-input min-h-[36px]">
      {children}
    </div>
  </div>
);

const CDL_CLASSES = ['US Class A', 'US Class B', 'US Class C'];

// Staff (admin/hr/accounting/dispatch) have no DriverProfile row at all --
// the driver-specific view below (DQF/equipment/bank info) doesn't apply
// to them, so this is a minimal parallel view: name/phone + password.
function StaffAccountPanel({ user }) {
  const { carrierId } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => {
    if (user?.id) load();
  }, [user?.id]);

  const load = async () => {
    setLoading(true);
    const results = await client.entities.StaffProfile.filter({ user_id: user.id }, '-created_date', 1);
    if (results.length > 0) {
      const p = results[0];
      // Older/onboarding-gate records may only have a combined
      // full_name (no separate first_name/last_name yet) -- split it
      // on the first space so editing an existing profile doesn't
      // show blank name boxes.
      let { first_name, last_name } = p;
      if (!first_name && !last_name && p.full_name) {
        const parts = p.full_name.trim().split(/\s+/);
        first_name = parts[0] || '';
        last_name = parts.slice(1).join(' ');
      }
      setProfile(p);
      setForm({ ...p, first_name, last_name });
    }
    setLoading(false);
  };

  const set = (key, value) => setForm(p => ({ ...p, [key]: value }));

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !profile) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file.'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB.'); return; }
    setUploadingAvatar(true);
    try {
      const url = await uploadAvatar(carrierId, user.id, file);
      await client.entities.StaffProfile.update(profile.id, { avatar_url: url });
      setForm(p => ({ ...p, avatar_url: url }));
      toast.success('Profile photo updated.');
    } catch (err) {
      toast.error(err?.message || 'Could not upload photo.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSave = async () => {
    if (!profile) return;
    if (!form.first_name?.trim() || !form.last_name?.trim()) {
      toast.error('First and last name are required.');
      return;
    }
    setSaving(true);
    await client.entities.StaffProfile.update(profile.id, {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      full_name: `${form.first_name.trim()} ${form.last_name.trim()}`,
      phone: form.phone,
      address: form.address,
      address2: form.address2,
      city: form.city,
      state: form.state,
      zip: form.zip,
    });
    toast.success('Profile updated successfully.');
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div>
        <h1 className="font-space text-2xl font-bold text-foreground">My Profile</h1>
        <p className="text-muted-foreground text-sm mt-1">Update your personal information and password.</p>
      </div>

      <div className="glass-card p-5 flex items-center gap-4">
        <label className="relative w-12 h-12 rounded-full flex-shrink-0 cursor-pointer group">
          {form.avatar_url ? (
            <img src={form.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
          ) : (
            <div className={cn('w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold', avatarColorClasses(user.id))}>
              {(form.full_name || user.email || '?').trim().slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
            {uploadingAvatar
              ? <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              : <Camera className="w-4 h-4 text-white" />}
          </div>
          <input type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} disabled={uploadingAvatar} />
        </label>
        <div>
          <p className="font-semibold text-foreground">{form.full_name || user.email}</p>
          <p className="text-sm text-muted-foreground">{user.email}</p>
          {profile?.staff_role && <p className="text-xs text-muted-foreground mt-0.5 capitalize">{profile.staff_role.replace('_', ' ')}</p>}
        </div>
      </div>

      <SectionCard icon={User} title="Contact Information">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="First Name" value={form.first_name} onChange={v => set('first_name', v)} placeholder="Jane" />
          <Field label="Last Name" value={form.last_name} onChange={v => set('last_name', v)} placeholder="Smith" />
          <div className="sm:col-span-2">
            <Field label="Phone Number" value={form.phone} onChange={v => set('phone', v)} placeholder="+1 (555) 000-0000" />
          </div>
          <div className="sm:col-span-2">
            <TrimbleAddressAutocomplete
              value={form.address}
              onChange={e => set('address', e.target.value)}
              onSelect={data => {
                set('address', data.street || form.address);
                if (data.city) set('city', data.city);
                if (data.state) set('state', data.state);
                if (data.zip) set('zip', data.zip);
              }}
              label="Address"
              placeholder="123 Main St"
            />
          </div>
          <div className="sm:col-span-2">
            <Field label="Address 2" value={form.address2} onChange={v => set('address2', v)} placeholder="Apt, suite, unit (optional)" />
          </div>
          <Field label="City" value={form.city} onChange={v => set('city', v)} placeholder="Las Vegas" />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">State</label>
              <select
                value={form.state ?? ''}
                onChange={e => set('state', e.target.value)}
                className="w-full h-10 px-3 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none"
              >
                <option value="" className="bg-background">—</option>
                {US_STATES.map(s => <option key={s} value={s} className="bg-background">{s}</option>)}
              </select>
            </div>
            <Field label="Zip" value={form.zip} onChange={v => set('zip', v)} placeholder="89110" />
          </div>
        </div>
        {/* Employee # is HR/Super Admin managed, not self-service --
            shown read-only here so staff can see it, not change it. */}
        {profile?.employee_number && (
          <ReadRow label="Employee #">{profile.employee_number}</ReadRow>
        )}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving || !profile} className="bg-primary text-primary-foreground">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </SectionCard>

      <AccountSecurityCard />
    </div>
  );
}

export default function DriverProfile() {
  const { user, isStaff } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  // Re-typed (never pasted) confirmation for a NEW bank account number --
  // pure UI state, never saved. Reset to blank every time the account
  // number is unlocked for editing so each edit session requires a fresh
  // confirmation, not whatever was left over from last time.
  const [bankAccountConfirm, setBankAccountConfirm] = useState('');
  const [assignedTruck, setAssignedTruck] = useState(null);
  const [dispatchFeePercent, setDispatchFeePercent] = useState(3);
  const [uploadingCdl, setUploadingCdl] = useState(false);
  const [uploadingMed, setUploadingMed] = useState(false);
  const [showTaxForm, setShowTaxForm] = useState(false);

  useEffect(() => {
    // Staff have no DriverProfile row -- StaffAccountPanel below handles
    // their own data loading instead.
    if (user?.email && !isStaff) loadProfile();
  }, [user?.email, isStaff]);

  useEffect(() => {
    if (isStaff) return;
    // Was hardcoded "3%" in the copy below regardless of what Accounting
    // configured in Pay & Deductions.
    client.entities.CompanySettings.filter({ key: 'dispatch_fee_percent' })
      .then(settings => setDispatchFeePercent(numOrDefault(settings[0]?.value, 3)))
      .catch(() => {});
  }, [isStaff]);

  const loadProfile = async () => {
    setLoading(true);
    // Case-insensitive lookup -- an exact-match filter here missed a
    // driver whenever their record's user_email casing didn't match
    // auth's exactly (e.g. admin-typed casing from DriverFormModal),
    // showing "No driver profile found" for a driver who very much had
    // one.
    const found = await findMyDriverProfile(user.email);
    if (found) {
      setProfile(found);
      setForm(found);
    }
    try {
      // Exact-match + no is_active filter here disagreed with
      // AdminPanel.jsx's truckUnitByDriverEmail (which lowercases both
      // sides and excludes a.is_active === false) -- a driver could see a
      // truck here that admin's own Loads table no longer considers
      // theirs, e.g. after the truck was deactivated or reassigned with
      // mismatched email casing.
      const emailLower = user.email.toLowerCase();
      const assets = await client.entities.Asset.list('-created_date', 200);
      setAssignedTruck(assets.find(a =>
        a.asset_type === 'Truck' &&
        a.is_active !== false &&
        (a.assigned_driver_email || '').toLowerCase() === emailLower
      ) || null);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const set = (key, value) => setForm(p => ({ ...p, [key]: value }));

  // Re-upload/re-scan your own CDL or Medical Card any time after
  // onboarding -- same AI auto-fill as the onboarding wizard
  // (StepCDLMedical.jsx), reusing the exact same DriverProfile fields
  // (cdl_number, cdl_expiration_date, med_card_expiration_date, etc.) that
  // DQFChecklist.jsx and the rest of HR/compliance already read, so an
  // update here shows up there automatically -- there's no separate copy
  // of this data to keep in sync.
  const handleCdlUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingCdl(true);
    toast.info('Uploading CDL...');
    try {
      const { file_url } = await client.integrations.Core.UploadFile({ file });
      set('cdl_document_url', file_url);
      toast.success('CDL uploaded — reading it now…');
      const { data, error } = await extractDriverCredential(file, 'cdl');
      if (data) {
        setForm(p => {
          const next = { ...p };
          Object.entries(data).forEach(([key, value]) => {
            if (value !== '' && value != null) next[key] = value;
          });
          return next;
        });
        toast.success('CDL details auto-filled below — please double-check them, then Save Changes.');
      } else if (error) {
        toast.info(error);
      }
    } catch (err) {
      toast.error(err?.message || 'Could not upload CDL.');
    } finally {
      setUploadingCdl(false);
    }
  };

  const handleMedUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingMed(true);
    toast.info('Uploading Medical Card...');
    try {
      const { file_url } = await client.integrations.Core.UploadFile({ file });
      set('med_card_document_url', file_url);
      toast.success('Medical Card uploaded — reading it now…');
      const { data, error } = await extractDriverCredential(file, 'med_card');
      if (data) {
        setForm(p => {
          const next = { ...p };
          Object.entries(data).forEach(([key, value]) => {
            if (value !== '' && value != null) next[key] = value;
          });
          return next;
        });
        toast.success('Medical Card details auto-filled below — please double-check them, then Save Changes.');
      } else if (error) {
        toast.info(error);
      }
    } catch (err) {
      toast.error(err?.message || 'Could not upload Medical Card.');
    } finally {
      setUploadingMed(false);
    }
  };

  if (isStaff) return <StaffAccountPanel user={user} />;

  const handleSave = async () => {
    if (!profile) {
      toast.error('No profile found. Please contact your dispatcher.');
      return;
    }
    // A changed bank account number must be confirmed by re-typing it --
    // blocks a save of a fat-fingered number that was only ever entered
    // once. Only applies when it's actually being changed, not on every
    // unrelated save (e.g. a phone number update) with the account number
    // still sitting in its unlocked state from an earlier click.
    if (showAccount && form.bank_account_number !== profile.bank_account_number) {
      if (!bankAccountConfirm) {
        toast.error('Please re-type your new bank account number to confirm it.');
        return;
      }
      if (bankAccountConfirm !== form.bank_account_number) {
        toast.error('Bank account numbers don\'t match. Please check and try again.');
        return;
      }
    }
    setSaving(true);
    // company_name/ein/dispatch_service are Owner-Operator-only (each
    // hidden below behind {!isCompanyDriver && ...}), but this save was
    // unconditionally re-persisting whatever value they last held
    // regardless of driver_type -- so any unrelated edit (e.g. a phone
    // number update) kept re-saving a stale dispatch_service: true (and
    // a real per-load dispatch fee kept getting deducted) or a stale
    // company_name/EIN for a driver who's since become a Company
    // Driver, with the driver unable to even see or clear these fields
    // themselves once hidden.
    const isCompanyDriverNow = profile.driver_type === 'Company Driver';
    await client.entities.DriverProfile.update(profile.id, {
      company_name: isCompanyDriverNow ? '' : form.company_name,
      phone: form.phone,
      address: form.address,
      cdl_number: form.cdl_number,
      ein: isCompanyDriverNow ? '' : form.ein,
      bank_routing_number: form.bank_routing_number,
      bank_account_number: form.bank_account_number,
      dispatch_service: isCompanyDriverNow ? false : (form.dispatch_service ?? false),
      // License & Medical Certificate -- same field set as onboarding's
      // StepCDLMedical.jsx, editable here too so a driver isn't stuck
      // with whatever they entered at onboarding forever.
      lic_first: form.lic_first,
      lic_last: form.lic_last,
      cdl_state: form.cdl_state,
      cdl_class: form.cdl_class,
      cdl_endorsements: form.cdl_endorsements,
      cdl_issued_date: form.cdl_issued_date || null,
      cdl_expiration_date: form.cdl_expiration_date || null,
      cdl_document_url: form.cdl_document_url,
      med_card_issue_date: form.med_card_issue_date || null,
      med_card_expiration_date: form.med_card_expiration_date || null,
      med_card_examiner_name: form.med_card_examiner_name,
      med_card_registry_number: form.med_card_registry_number,
      med_card_document_url: form.med_card_document_url,
    });
    // Without this, `profile` (used above to detect "did the account
    // number actually change") stayed stuck at whatever was loaded on
    // page open -- so a second, unrelated save right after a successful
    // account number change would wrongly demand re-confirming it again,
    // since the just-saved new value would still look "different" from
    // the stale profile snapshot.
    setProfile(p => ({ ...p, bank_account_number: form.bank_account_number }));
    setShowAccount(false);
    setBankAccountConfirm('');
    toast.success('Profile updated successfully.');
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-xl mx-auto mt-12 glass-card p-10 text-center">
        <User className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
        <p className="text-foreground font-semibold">No driver profile found</p>
        <p className="text-muted-foreground text-sm mt-1">Contact your dispatcher to set up your profile.</p>
      </div>
    );
  }

  const isCompanyDriver = profile.driver_type === 'Company Driver';

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div>
        <h1 className="font-space text-2xl font-bold text-foreground">My Profile</h1>
        <p className="text-muted-foreground text-sm mt-1">View and update your personal, payment, and equipment details.</p>
      </div>

      {/* YTD Earnings */}
      {(profile.ytd_gross_earnings > 0 || profile.ytd_net_earnings > 0) && (
        <div className="grid grid-cols-2 gap-4">
          <div className="glass-card p-4">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center mb-2">
              <DollarSign className="w-4 h-4 text-primary" />
            </div>
            <p className="text-xl font-space font-bold text-foreground">${(profile.ytd_gross_earnings || 0).toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-0.5">YTD Gross Revenue</p>
          </div>
          <div className="glass-card p-4">
            <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center mb-2">
              <TrendingUp className="w-4 h-4 text-green-400" />
            </div>
            <p className="text-xl font-space font-bold text-foreground">${(profile.ytd_net_earnings || 0).toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-0.5">YTD Net Take-Home</p>
          </div>
        </div>
      )}

      {/* Read-only identity */}
      <div className="glass-card p-5 flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
          <User className="w-6 h-6 text-primary" />
        </div>
        <div>
          <p className="font-semibold text-foreground">{profile.full_name}</p>
          <p className="text-sm text-muted-foreground">{profile.user_email}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{profile.truck_type ? `${profile.truck_type} ` : ''}Driver</p>
        </div>
      </div>

      {/* Missing W-4 (Company Driver) / W-9 (Owner Operator) -- nothing
          revisits this after onboarding otherwise, so a driver who was
          created without going through it (or skipped it) has no way to
          ever fill it in. */}
      {(isCompanyDriver ? !profile.w4_signature : !profile.w9_signature) && (
        <div className="glass-card p-5 border-amber-500/30 bg-amber-500/5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Complete your {isCompanyDriver ? 'W-4' : 'W-9'}</p>
              <p className="text-xs text-muted-foreground">Required for tax withholding — {isCompanyDriver ? 'W-2 wages' : '1099 reporting'}.</p>
            </div>
          </div>
          <Button onClick={() => setShowTaxForm(true)} size="sm" className="bg-amber-500 text-black hover:bg-amber-400">
            Complete Now
          </Button>
        </div>
      )}

      {/* Company Driver: assigned truck */}
      {isCompanyDriver && (
        <SectionCard icon={Truck} title="Your Assignment">
          <ReadRow label="Assigned Truck">
            {assignedTruck
              ? `Unit ${assignedTruck.unit_number}${[assignedTruck.year, assignedTruck.make, assignedTruck.model].filter(Boolean).length ? ' · ' + [assignedTruck.year, assignedTruck.make, assignedTruck.model].filter(Boolean).join(' ') : ''}`
              : <span className="text-muted-foreground">Not yet assigned — contact your dispatcher</span>}
          </ReadRow>
        </SectionCard>
      )}

      {/* Commercial Driver's License -- upload a photo/scan and these
          fields auto-fill (AI-read, always worth double-checking).
          Same fields as onboarding's CDL step, and the SAME
          DriverProfile row DQFChecklist/Pending Approvals/compliance
          reports already read -- editing here updates the HR side too. */}
      <SectionCard icon={IdCard} title="Commercial Driver's License (CDL)">
        <UploadField label="Upload CDL (Front & Back)" url={form.cdl_document_url} uploading={uploadingCdl} onUpload={handleCdlUpload} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="First Name (as on license)" value={form.lic_first} onChange={v => set('lic_first', v)} />
          <Field label="Last Name (as on license)" value={form.lic_last} onChange={v => set('lic_last', v)} />
          <Field label="CDL Number" value={form.cdl_number} onChange={v => set('cdl_number', v)} />
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Issuing State</label>
            <select
              value={form.cdl_state ?? ''}
              onChange={e => set('cdl_state', e.target.value)}
              className="w-full h-10 px-3 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none"
            >
              <option value="" className="bg-background">—</option>
              {US_STATES.map(s => <option key={s} value={s} className="bg-background">{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">CDL Class</label>
            <select
              value={form.cdl_class ?? ''}
              onChange={e => set('cdl_class', e.target.value)}
              className="w-full h-10 px-3 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none"
            >
              <option value="" className="bg-background">—</option>
              {CDL_CLASSES.map(c => <option key={c} value={c} className="bg-background">{c}</option>)}
            </select>
          </div>
          <Field label="Endorsements" value={form.cdl_endorsements} onChange={v => set('cdl_endorsements', v)} placeholder="e.g. H, N, T" />
          <Field label="Issue Date" type="date" value={form.cdl_issued_date} onChange={v => set('cdl_issued_date', v)} />
          <Field label="Expiration Date" type="date" value={form.cdl_expiration_date} onChange={v => set('cdl_expiration_date', v)} />
        </div>
      </SectionCard>

      {/* DOT Medical Certificate -- same auto-fill-from-upload pattern. */}
      <SectionCard icon={HeartPulse} title="Medical Certificate">
        <UploadField label="Upload Medical Card (Front)" url={form.med_card_document_url} uploading={uploadingMed} onUpload={handleMedUpload} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Issue Date" type="date" value={form.med_card_issue_date} onChange={v => set('med_card_issue_date', v)} />
          <Field label="Expiration Date" type="date" value={form.med_card_expiration_date} onChange={v => set('med_card_expiration_date', v)} />
          <Field label="Medical Examiner Name" value={form.med_card_examiner_name} onChange={v => set('med_card_examiner_name', v)} placeholder="e.g. Dr. Michelle Guidry" />
          <Field label="National Registry Number" value={form.med_card_registry_number} onChange={v => set('med_card_registry_number', v)} placeholder="e.g. 1278764696" />
        </div>
        <p className="text-xs text-muted-foreground mt-1">Keep your CDL and medical card current — notify your dispatcher before they expire.</p>
      </SectionCard>

      {/* Contact Information */}
      <SectionCard icon={User} title="Contact Information">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* HR/Super Admin managed, not self-service -- same pattern as staff. */}
          {profile?.employee_number && (
            <ReadRow label="Employee #">{profile.employee_number}</ReadRow>
          )}
          {!isCompanyDriver && (
            <Field label="Company Name (LLC/Corp)" value={form.company_name} onChange={v => set('company_name', v)} placeholder="e.g. Smith Trucking LLC" />
          )}
          <Field label="Phone Number" value={form.phone} onChange={v => set('phone', v)} placeholder="+1 (555) 000-0000" />
          <div className="sm:col-span-2">
            <TrimbleAddressAutocomplete
              value={form.address}
              onChange={e => set('address', e.target.value)}
              onSelect={data => set('address', data.fullAddress || form.address)}
              label="Address"
              placeholder="123 Main St, City, State 12345"
            />
          </div>
        </div>
      </SectionCard>

      {/* Credentials & Payment */}
      <SectionCard icon={CreditCard} title="Credentials & Payment">
        <p className="text-xs text-amber-400/80 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
          Your banking details are stored securely and used only for settlement payments.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* CDL Number now lives in the Commercial Driver's License
              section above, alongside the rest of the CDL fields. */}
          {!isCompanyDriver && (
            <Field label="EIN (Employer Identification #)" value={form.ein} onChange={v => set('ein', v)} placeholder="XX-XXXXXXX" />
          )}
          {/* autoComplete="off" on both -- neither field has a real
              autocomplete purpose, but leaving it unset let Chrome/Android's
              autofill guess at them (e.g. dropping in a saved email), which
              then got typed straight into real bank fields on Save. */}
          <Field label="Bank Routing Number" value={form.bank_routing_number} onChange={v => set('bank_routing_number', v)} autoComplete="off" />
          <div className="sm:col-span-2">
            <label className="text-xs text-muted-foreground block mb-1">Bank Account Number</label>
            <div className="relative">
              <Input
                type="text"
                autoComplete="off"
                onPaste={e => e.preventDefault()}
                value={showAccount
                  ? (form.bank_account_number ?? '')
                  : form.bank_account_number
                    ? '•••• •••• •••• ' + String(form.bank_account_number).slice(-4)
                    : ''}
                onChange={e => showAccount && set('bank_account_number', e.target.value)}
                readOnly={!showAccount}
                placeholder="Not set"
                className="text-foreground pr-10"
              />
              <button
                type="button"
                onClick={() => {
                  // Every unlock starts a fresh confirmation -- otherwise a
                  // stale match from a previous edit could let a new,
                  // mistyped number slip through unconfirmed.
                  setBankAccountConfirm('');
                  setShowAccount(v => !v);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showAccount ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {!showAccount && form.bank_account_number && (
              <p className="text-xs text-muted-foreground mt-1">Click the eye icon to reveal and edit your account number.</p>
            )}
            {/* Must be re-typed, not pasted -- catches a fat-fingered digit
                that pasting the same (possibly wrong) clipboard value twice
                never would. Only required when the number is actually
                being changed, not just viewed. */}
            {showAccount && form.bank_account_number !== profile.bank_account_number && (
              <div className="mt-2">
                <label className="text-xs text-muted-foreground block mb-1">Confirm Bank Account Number (re-type, don't paste)</label>
                <Input
                  type="text"
                  autoComplete="off"
                  onPaste={e => e.preventDefault()}
                  value={bankAccountConfirm}
                  onChange={e => setBankAccountConfirm(e.target.value)}
                  placeholder="Re-type to confirm"
                  className={cn('text-foreground', bankAccountConfirm && bankAccountConfirm !== form.bank_account_number && 'border-red-500/50')}
                />
                {bankAccountConfirm && bankAccountConfirm !== form.bank_account_number && (
                  <p className="text-xs text-red-400 mt-1">Doesn't match the account number above.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </SectionCard>

      {/* Dispatch Service — owner-operator opt-in; not applicable to company drivers */}
      {!isCompanyDriver && (
      <SectionCard icon={Headphones} title="Dispatch Service">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">Enable Dispatch Service</p>
            <p className="text-xs text-muted-foreground mt-0.5">{dispatchFeePercent}% of gross rate per load — we handle finding & booking your loads</p>
          </div>
          <button
            type="button"
            onClick={() => set('dispatch_service', !form.dispatch_service)}
            className={cn(
              "relative w-11 h-6 rounded-full transition-colors duration-200 flex-shrink-0",
              form.dispatch_service ? "bg-primary" : "bg-white/20"
            )}
          >
            <span className={cn(
              "absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200",
              form.dispatch_service ? "translate-x-5" : "translate-x-0"
            )} />
          </button>
        </div>
        {form.dispatch_service && (
          <p className="text-xs text-primary bg-primary/10 border border-primary/20 rounded-lg px-3 py-2">
            Dispatch fee of {dispatchFeePercent}% will be automatically deducted from every load settlement.
          </p>
        )}
      </SectionCard>
      )}

      {/* Equipment Info — read-only, managed by carrier */}
      <SectionCard icon={Truck} title="Equipment Info">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            ['Truck Info', form.truck_info],
            ['Trailer Info', form.trailer_info],
            ['Truck VIN', form.truck_vin],
            ['License Plate', form.license_plate],
          ].map(([label, value]) => (
            <div key={label}>
              <label className="text-xs text-muted-foreground block mb-1">{label}</label>
              <p className="text-foreground text-sm py-2 px-3 rounded-lg bg-transparent border border-input min-h-[36px]">
                {value || <span className="text-muted-foreground">—</span>}
              </p>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-1">Equipment information is managed by your carrier. Contact your dispatcher to update.</p>
      </SectionCard>

      <div className="flex justify-end pb-4">
        <Button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground px-8">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <AccountSecurityCard />

      {showTaxForm && (
        <CompleteTaxFormModal
          driver={profile}
          onClose={() => setShowTaxForm(false)}
          onSaved={loadProfile}
        />
      )}
    </div>
  );
}